/* -------------------------- Design imports start -------------------------- */
import type * as Communicator from '@hoops/web-viewer'
import { Box, Button, Slider, Stack } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { type ReactElement, useState } from "react";

/* ------------------------- Functional imports end ------------------------- */

type Props = {
  hoopsExplodeManager: Communicator.ExplodeManager
  maxMagnitude?: number
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function HoopsExplodeButton(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { hoopsExplodeManager, maxMagnitude = 1.5 } = props
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [openExplodeSlider, setOpenExplodeSlider] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [magnitude, setMagnitude] = useState<number>(0)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */

  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */

  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleToggleExplodeSlider = (): void => {
    setOpenExplodeSlider(prev => !prev)
  }

  const handleMagnitudeChange = (_: any, newValue: number | number[]): void => {
    if (Array.isArray(newValue)) {
      throw new Error('HoopsExplodeButton only supports one single magnitude slider.')
    }
    setMagnitude(newValue)
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const popOverHeight = '130px'
  const magnitudeKeys = [
    {
      label: '100%',
      factor: 1,
    },
    {
      label: '50%',
      factor: 0.5,
    },
    {
      label: '0%',
      factor: 0,
    },
  ]
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  // sync magnitude and the provided Hoops WebViewer instance
  void (async () => await hoopsExplodeManager.setMagnitude(magnitude))()
  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Box position="relative">
      <Button
        variant="outlined"
        color="primary"
        onClick={handleToggleExplodeSlider}
        sx={{
          textTransform: 'none',
        }}
      >
        Explode
      </Button>
      {openExplodeSlider && (
        <Box
          id="explodeSliderContainer"
          position="absolute"
          top={`calc(-${popOverHeight} - 8px)`}
          left="50%"
          sx={{
            backgroundColor: 'white',
            border: '1px solid lightgray',
            borderRadius: '5px',
            transform: 'translateX(-50%)',
          }}
        >
          <Stack direction="row" spacing={0} p="16px 8px" height={popOverHeight}>
            <Slider
              orientation="vertical"
              min={0}
              max={maxMagnitude}
              step={0.05}
              value={magnitude}
              onChange={handleMagnitudeChange}
            />
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              mt="-12px"
              mb="-12px"
              mr="-6px"
            >
              {magnitudeKeys.map(key => (
                <Button
                  key={`${key.label}MagnitudeButton`}
                  color="primary"
                  onClick={() => setMagnitude(key.factor * maxMagnitude)}
                >
                  {key.label}
                </Button>
              ))}
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  )
}
